import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import {
  Combobox,
  ComboboxInput,
  ComboboxInputGroup,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePreferencesStore } from '@/shared/store/preferences';
import {
  GROUP_LABEL,
  GROUP_ORDER,
  buildEntries,
  contentsSearchable,
  matches,
  type ActionId,
  type EntryGroup,
  type EntrySources,
  type PaletteEntry,
} from '../entries';
import { openingEntries } from '../recents';

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: EntrySources;
  /** Runs an action. The palette never reimplements one — see FR-015. */
  onAction: (action: ActionId, arg?: string) => void;
}

/** How many rows one group may draw before the rest are counted (FR-013). */
const PER_GROUP = 6;

/**
 * One keyboard entry point to everything the application can already reach (spec 077).
 *
 * A dialog wrapping the existing combobox, which supplies the three things that make a
 * palette hard and easy to get subtly wrong: filtering, keyboard navigation, and the
 * ARIA combobox roles with active-descendant behaviour. None of that is reimplemented
 * here (research R3).
 *
 * **The entries are not authored.** Pages come from the app-shell navigation registry,
 * gated by the same `visibleNavItems` both shells use; workspaces and workspace contents
 * come from queries already in cache. The palette causes no request of its own (FR-024).
 */
export function CommandPalette({
  open,
  onOpenChange,
  sources,
  onAction,
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const recentIds = usePreferencesStore((state) => state.recentEntryIds);
  const rememberEntry = usePreferencesStore((state) => state.rememberEntry);

  const all = useMemo(() => buildEntries(sources), [sources]);

  const shown = useMemo(() => {
    // Before anything is typed, recents — falling back to something useful, because an
    // empty palette teaches a first-time reader nothing (FR-021).
    if (query.trim() === '') return openingEntries(recentIds, all);
    return all.filter((entry) => matches(entry, query));
  }, [all, query, recentIds]);

  const grouped = useMemo(() => groupEntries(shown), [shown]);
  const searchable = contentsSearchable(sources);

  function run(entry: PaletteEntry) {
    rememberEntry(entry.id);
    onOpenChange(false);
    setQuery('');

    if (entry.act) {
      onAction(entry.act, entry.actArg);
      return;
    }
    if (entry.to) {
      void navigate({
        to: entry.to,
        ...(entry.params ? { params: entry.params } : {}),
        ...(entry.search ? { search: entry.search } : {}),
      } as never);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        // A query left standing means reopening shows a list filtered by a word the
        // reader typed a minute ago and can no longer see.
        if (!next) setQuery('');
      }}
    >
      <DialogContent
        showCloseButton={false}
        /*
          `overflow-hidden` because this dialog sets `p-0` and its last child is
          the scrolling list, so the list's rows — and its scrollbar — painted
          straight over the `rounded-xl` corners the dialog draws. A container
          that is round everywhere except where its content reaches the edge
          reads as a rendering fault, and it is at its most obvious here because
          the list is *always* at the edge.
        */
        className="top-[15%] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-lg max-md:pt-4"
        // Full height on a phone (spec 261): the field at the top, the list
        // under it, both above the keyboard a bottom-anchored sheet sits under.
        phoneLayout="full"
      >
        <DialogTitle className="sr-only">Search and commands</DialogTitle>
        <DialogDescription className="sr-only">
          Find a workspace, a page or something in this workspace, or run a
          command.
        </DialogDescription>

        <Combobox
          open
          onOpenChange={(next: boolean) => {
            /*
              Escape must close the *palette*, not just the list (FR-004).

              The combobox is permanently open — it is the palette's body, not a popup —
              so it owns the dismissal gesture and swallowed Escape entirely: the dialog
              never heard it, the overlay stayed, and the palette could not be closed by
              keyboard at all. Forwarding the close is what makes the two one surface.

              Found by opening it in a browser. Every unit test passed with the palette
              unclosable, because none of them pressed Escape — which is the argument for
              looking at a thing as well as testing it.
            */
            if (!next) onOpenChange(false);
          }}
          items={shown.map((entry) => entry.id)}
          // Typing then Enter runs the first match (spec 129): with nothing
          // highlighted, Enter after "add secret" did nothing at all.
          autoHighlight
          value={null}
          onValueChange={(id: string | null) => {
            const entry = shown.find((candidate) => candidate.id === id);
            if (entry) run(entry);
          }}
          itemToStringLabel={(id: string) =>
            shown.find((entry) => entry.id === id)?.label ?? id
          }
        >
          <ComboboxInputGroup className="border-border m-0 rounded-none border-0 border-b px-3">
            <ComboboxInput
              autoFocus
              aria-label="Search and commands"
              placeholder="Go to, or run…"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
            />
          </ComboboxInputGroup>

          {/*
            The count, announced. Base UI gives the combobox roles and
            active-descendant behaviour; what it cannot know is that a list changing
            silently as someone types tells a blind reader nothing (FR-026).
          */}
          <p aria-live="polite" className="sr-only">
            {`${String(shown.length)} ${shown.length === 1 ? 'result' : 'results'}`}
          </p>

          {/*
            `scrollbar-gutter-stable`, for the reason the shell's `<main>` has it
            — and more acutely. This list is refiltered on every keystroke, so it
            crosses `max-h-80` constantly: the scrollbar appears and disappears
            as you type, and without a reserved gutter every row shifts sideways
            underneath the reader while they are still typing.
          */}
          <ComboboxList className="scrollbar-slim scrollbar-gutter-stable max-h-80 p-1 max-md:max-h-[calc(100dvh-8rem)]">
            {shown.length === 0 ? (
              <p className="text-muted-foreground px-3 py-6 text-center text-sm">
                Nothing matches “{query}”.
              </p>
            ) : (
              GROUP_ORDER.filter((group) => grouped.has(group)).map((group) => {
                const entries = grouped.get(group) ?? [];
                const drawn = entries.slice(0, PER_GROUP);
                const hidden = entries.length - drawn.length;

                return (
                  <div key={group} className="py-1">
                    <p className="text-muted-foreground px-2 pb-1 text-xs font-medium tracking-wider uppercase">
                      {GROUP_LABEL[group]}
                    </p>
                    {drawn.map((entry) => (
                      <ComboboxItem key={entry.id} value={entry.id}>
                        <entry.icon
                          aria-hidden="true"
                          className="size-4 shrink-0"
                          strokeWidth={1.5}
                        />
                        <span className="truncate">{entry.label}</span>
                        {/*
                          A workflow and its board share a name by construction, and a
                          task may share one with a page. The kind is what tells them
                          apart (FR-010).
                        */}
                        {entry.detail ? (
                          <span className="text-muted-foreground ms-auto shrink-0 text-xs">
                            {entry.detail}
                          </span>
                        ) : null}
                      </ComboboxItem>
                    ))}
                    {hidden > 0 ? (
                      // Counted, never silently dropped — a capped list that says
                      // nothing is indistinguishable from a complete one (FR-013).
                      <p className="text-muted-foreground px-2 py-1 text-xs">
                        and {String(hidden)} more
                      </p>
                    ) : null}
                  </div>
                );
              })
            )}

            {/*
              FR-012, and the reason it is a requirement rather than a nicety: reporting
              "no matches" when nothing was searched is a lie the reader cannot detect.
              Only the current workspace's contents are held, so outside one the palette
              says what it did *not* look at.
            */}
            {!searchable && query.trim() !== '' ? (
              <p className="text-muted-foreground border-border border-t px-3 py-2 text-xs">
                Workspace contents were not searched — open a workspace to
                search inside it.
              </p>
            ) : null}
          </ComboboxList>
        </Combobox>
      </DialogContent>
    </Dialog>
  );
}

function groupEntries(
  entries: PaletteEntry[],
): Map<EntryGroup, PaletteEntry[]> {
  const grouped = new Map<EntryGroup, PaletteEntry[]>();
  for (const entry of entries) {
    const bucket = grouped.get(entry.group);
    if (bucket) bucket.push(entry);
    else grouped.set(entry.group, [entry]);
  }
  return grouped;
}
